import React, { useEffect, useState } from "react";
import { createStyles } from "@material-ui/core/styles";
import withStyles from "@material-ui/core/styles/withStyles";
import {
  WithStyles,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useTranslate } from "../../../services/appLanguageService";
import store from "../../../redux/store";
import { setTitle } from "../../../redux/reducers/tabRouter/tabTitle/actions";
import { BaseAccessory, Contract, Accessory } from "../../../redux/types";
import EditableTextField from "../../EditableTextField";
import EditableCheckBoxField from "../../EditableCheckBoxField";
import { niceDate } from "../../FormatHelpers";
import ResourceLink from "../../links/ResourceLink";
import {
  getAccessoryAPI,
  getBaseAccessoryAPI,
  patchAccessoryAPI
} from "../../../services/api-declaration";
import * as Yup from "yup";
import SimpleImageUpload from "../../imageUploadComponent/SimpleImageUpload";
import LockIcon from "@material-ui/icons/Lock";
import ContractItemTask from "../ContractItemTask";
import LoadingSpinnerPaper from "../../../components/LoadingSpinnerPaper";

const styles = (theme: any) =>
  createStyles({
    root: {
      flexGrow: 1
    },
    paper: {
      marginTop: 20
    },
    editButton: {
      marginLeft: 20
    },
    pageNotFound: {
      marginTop: 20,
      padding: 20
    },
    expandHeader: {
      color: theme.palette.text.secondary
    },
    logo: {
      maxWidth: 300,
      maxHeight: 300
    },
    bold: {
      fontWeight: "bold"
    },
    tableContainer: {
      padding: 0
    }
  });

interface AccessoryProps extends WithStyles<typeof styles> {
  tabId: number;
  accessory: Accessory;
  contract: Contract | undefined;
  businessAreaNames: Map<number, string>;
}

const AccessoryView: React.FC<AccessoryProps> = (props) => {
  const { classes, tabId, contract, accessory:initialAccessory, businessAreaNames } = props;
  const t = useTranslate("AccessoryPage");
  const t3 = useTranslate("ValidationErrorMessages");

  const [baseAccessory, setBaseAccessory] = useState<BaseAccessory>();
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [accessory, setAccessory] = useState<Accessory>(initialAccessory);
  const [expanded, setExpanded] = useState(false);
  const updateResourceState = (value: Accessory) => setAccessory(value);

  const patchUrl = `contracts/accessories/${initialAccessory.id}/`;

  useEffect(() => {
    if (accessory) store.dispatch(setTitle(accessory.name, tabId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessory]);

  useEffect(() => {
    let alive = true;

    if (contract?.id) {
      (async () => {
        const accessory = initialAccessory;
        const baseAccessoryResponse = await getBaseAccessoryAPI(accessory.base);

        if (alive) {
          setBaseAccessory(baseAccessoryResponse);
        }
      })();
    }
    return () => {
      alive = false;
    };
  }, [contract?.id, initialAccessory]);

  const accessoryIsLocked =
    contract?.locked_items["accessories"].includes(initialAccessory.id) || false;

  const addImage = async (image: string) => {
    setImageLoading(true);
    const response = await patchAccessoryAPI(initialAccessory.id, {
      image: image
    });

    updateResourceState(response);
    setImageLoading(false);
  };

  const deleteImage = async () => {
    setImageLoading(true);
    const response = await patchAccessoryAPI(initialAccessory.id, {
      image: null
    });

    updateResourceState(response);
    setImageLoading(false);
  };

  if (!accessory) return <LoadingSpinnerPaper />;

  return accessory.contract !== contract?.id ? (
    <Paper className={classes.pageNotFound}>
      <Typography variant="h6">{t("pageNotFoundTitle")}</Typography>
    </Paper>
  ) : (
    <div className={classes.root}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TableContainer component={Paper} className={classes.paper}>
            <Table size="small" aria-label="a dense table">
              <TableBody>
                <TableRow key={0}>
                  <TableCell align="left" className={classes.bold} colSpan={2}>
                    <SimpleImageUpload
                      handleImageSave={addImage}
                      handleImageDelete={deleteImage}
                      image={accessory.image}
                      alt={accessory.name}
                      isLoading={imageLoading}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={1}>
                  <TableCell align="left" colSpan={2}>
                    <Typography variant="h5">
                      {contract?.name}: <b>{accessory.name}</b>{" "}
                      {accessoryIsLocked && (
                        <Tooltip title={t("accessoryLockedLabel")}>
                          <LockIcon />
                        </Tooltip>
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow key={2}>
                  <TableCell align="left" className={classes.bold}>
                    {t("nameLabel")}
                  </TableCell>
                  <TableCell align="right">
                    {accessoryIsLocked ? (
                      accessory.name
                    ) : (
                      <EditableTextField
                        field="name"
                        initValue={accessory.name}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                        validation={Yup.string()
                          .min(1, t3("minLengthError"))
                          .max(255, t3("max255LengthError"))
                          .required(t3("requiredError"))}
                      />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow key={3}>
                  <TableCell align="left" className={classes.bold}>
                    {t("contractLabel")}
                  </TableCell>
                  <TableCell align="right">
                    {contract && (
                      <ResourceLink
                        label={contract.name}
                        id={contract.id}
                        url="contracts/contracts"
                      />
                    )}
                  </TableCell>
                </TableRow>
                {accessory.price !== null && (
                  <TableRow key={4}>
                    <TableCell align="left" className={classes.bold}>
                      {t("priceLabel")}
                    </TableCell>
                    <TableCell align="right">
                      {accessoryIsLocked ? (
                        accessory.price
                      ) : (
                        <EditableTextField
                          isPrice
                          field="price"
                          initValue={accessory.price}
                          url={patchUrl}
                          updateResourceState={updateResourceState}
                          number={true}
                          validation={Yup.number()
                            .typeError(t3("numberRequiredError"))
                            .min(0, t3("minZeroError"))
                            .max(10000000, t3("max10000000NumberError"))
                            .required(t3("requiredError"))}
                        />
                      )}{" "}
                      kr {accessory.unit ? `/ ${accessory.unit}` : ""}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow key={5}>
                  <TableCell align="left" className={classes.bold}>
                    {t("baseAccessoryLabel")}
                  </TableCell>
                  <TableCell align="right">
                    {baseAccessory && (
                      <ResourceLink
                        url="contracts/baseaccessories"
                        label={baseAccessory.name}
                        id={baseAccessory.id}
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              className={classes.expandHeader}
              onClick={() =>
                expanded ? setExpanded(false) : setExpanded(true)
              }
            >
              <Typography>
                {expanded ? t("hideLabel") : t("showMoreLabel")}
              </Typography>
            </AccordionSummary>
            <TableContainer
              component={AccordionDetails}
              className={classes.tableContainer}
            >
              <Table size="small" aria-label="a dense table">
                <TableBody>
                  <TableRow key={0}>
                    <TableCell align="left" className={classes.bold}>
                      {t("volumeLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableTextField
                        field="volume"
                        initValue={accessory.volume}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                        number={true}
                        validation={Yup.number()
                          .typeError(t3("numberRequiredError"))
                          .min(0, t3("minZeroError"))
                          .max(100000, t3("max100000NumberError"))
                          .nullable()
                          .notRequired()}
                      />{" "}
                      m<sup>3</sup>
                    </TableCell>
                  </TableRow>
                  <TableRow key={1}>
                    <TableCell align="left" className={classes.bold}>
                      {t("widthLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableTextField
                        field="width"
                        initValue={accessory.width}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                        number={true}
                        validation={Yup.number()
                          .typeError(t3("numberRequiredError"))
                          .min(0, t3("minZeroError"))
                          .max(100000, t3("max100000NumberError"))
                          .nullable()
                          .notRequired()}
                      />{" "}
                      mm
                    </TableCell>
                  </TableRow>
                  <TableRow key={2}>
                    <TableCell align="left" className={classes.bold}>
                      {t("heightLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableTextField
                        field="height"
                        initValue={accessory.height}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                        number={true}
                        validation={Yup.number()
                          .typeError(t3("numberRequiredError"))
                          .min(0, t3("minZeroError"))
                          .max(100000, t3("max100000NumberError"))
                          .nullable()
                          .notRequired()}
                      />{" "}
                      mm
                    </TableCell>
                  </TableRow>
                  <TableRow key={3}>
                    <TableCell align="left" className={classes.bold}>
                      {t("weightLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableTextField
                        field="weight"
                        initValue={accessory.weight}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                        number={true}
                        validation={Yup.number()
                          .typeError(t3("numberRequiredError"))
                          .min(0, t3("minZeroError"))
                          .max(100000, t3("max100000NumberError"))
                          .nullable()
                          .notRequired()}
                      />{" "}
                      kg
                    </TableCell>
                  </TableRow>
                  <TableRow key={4}>
                    <TableCell align="left" className={classes.bold}>
                      {t("rangeLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableTextField
                        field="range"
                        initValue={accessory.range}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                        number={true}
                        validation={Yup.number()
                          .typeError(t3("numberRequiredError"))
                          .min(0, t3("minZeroError"))
                          .max(100000, t3("max100000NumberError"))
                          .nullable()
                          .notRequired()}
                      />{" "}
                      km
                    </TableCell>
                  </TableRow>
                  <TableRow key={5}>
                    <TableCell align="left" className={classes.bold}>
                      {t("notesLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableTextField
                        field="notes"
                        initValue={accessory.notes}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                        validation={Yup.string().nullable().notRequired()}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow key={6}>
                    <TableCell align="left" className={classes.bold}>
                      {t("displayLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableCheckBoxField
                        field="display"
                        label={t("displayLabel")}
                        initValue={accessory.display}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow key={7}>
                    <TableCell align="left" className={classes.bold}>
                      {t("orderByLabel")}
                    </TableCell>
                    <TableCell align="right">
                      <EditableTextField
                        field="order_by"
                        initValue={accessory.order_by}
                        url={patchUrl}
                        updateResourceState={updateResourceState}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow key={8}>
                    <TableCell align="left" className={classes.bold}>
                      {t("createdAtLabel")}
                    </TableCell>
                    <TableCell align="right">
                      {niceDate(accessory.created_at)}
                    </TableCell>
                  </TableRow>
                  <TableRow key={9}>
                    <TableCell align="left" className={classes.bold}>
                      {t("updatedAtLabel")}
                    </TableCell>
                    <TableCell align="right">
                      {niceDate(accessory.updated_at)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Accordion>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ContractItemTask taskId={accessory.task}  businessAreaNames={businessAreaNames}/>
        </Grid>
      </Grid>
    </div>
  );
};

export default withStyles(styles)(AccessoryView);
